import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Bell, CheckCheck } from 'lucide-react';
import { getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead } from '../services/api';

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      // Silently fail on polling
    }
  }, []);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    try {
      const data = await getNotifications(1);
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkRead = async (uuid) => {
    try {
      await markNotificationRead(uuid);
      setNotifications(notifications.map(n => 
        n.uuid === uuid ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const open = Boolean(anchorEl);

  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ mr: 1 }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <Bell size={20} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 360, maxHeight: 480, borderRadius: 2, mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="600">
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<CheckCheck size={14} />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Bell size={32} color="#9e9e9e" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 380, overflow: 'auto' }}>
            {notifications.map((notif) => (
              <ListItem
                key={notif.uuid}
                sx={{
                  bgcolor: notif.read ? 'transparent' : 'action.hover',
                  cursor: notif.read ? 'default' : 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                }}
                onClick={() => !notif.read && handleMarkRead(notif.uuid)}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={notif.read ? 400 : 600}>
                      {notif.title}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" component="span">
                        {notif.message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.5 }}>
                        {getTimeAgo(notif.created_at)}
                      </Typography>
                    </Box>
                  }
                />
                {!notif.read && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main', ml: 1, flexShrink: 0 }} />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
